/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useRetracked } from 'js/lib/retracked';

import { Link, Typography2, VisuallyHidden } from '@coursera/cds-core';
import { CheckIcon } from '@coursera/cds-icons';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  href: string;
  isCurrent: boolean;
  name: string;
  productId?: string;
  description?: string | null;
  logo?: React.ReactNode;
  onClick?: (e?: React.MouseEvent<HTMLElement>, productId?: string) => void;
  trackingName?: string;
};

export const LOGO_WIDTH = 36;

const styles = {
  programCard: (isCurrent: boolean) => css`
    padding: var(--cds-spacing-100);
    display: flex;
    gap: var(--cds-spacing-100);
    align-items: center;
    justify-content: space-between;
    border-radius: var(--cds-border-radius-50);
    background-color: ${isCurrent ? 'var(--cds-color-grey-25)' : 'transparent'};

    /* override box-shadow from .cds-link-focusVisible */
    box-shadow: none !important;

    :hover {
      text-decoration: none;
      background-color: var(--cds-color-grey-25);
    }

    :active {
      text-decoration: none;
    }

    :focus {
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: -2px;
      text-decoration: none;
    }
  `,
  program: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-shrink: 1;
    min-width: 0;
  `,
  programName: css`
    min-width: 0;
  `,
  logo: css`
    width: ${LOGO_WIDTH}px;
    text-align: center;
    flex-shrink: 0;
  `,
  checkIcon: css`
    justify-self: end;
    flex-shrink: 0;
  `,
  oneLine: css`
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  `,
};

const ProgramCard = ({ href, isCurrent, name, productId, description, logo, onClick, trackingName }: Props) => {
  const track = useRetracked();

  return (
    <Link
      href={href}
      onClick={(e) => {
        track({
          trackingName: trackingName ?? 'program_switcher_link',
          action: 'click',
          trackingData: { name, href },
        });
        onClick?.(e, productId);
      }}
      css={styles.programCard(isCurrent)}
      variant="quiet"
      data-testid={name === 'Coursera' ? 'CourseraMembershipCard' : 'MembershipCard'}
    >
      <div css={styles.program}>
        {logo && <div css={styles.logo}>{logo}</div>}
        <div css={styles.programName}>
          <Typography2 variant="bodySecondary" component="div" css={styles.oneLine}>
            {name}
          </Typography2>
          {description && (
            <Typography2 component="p" variant="bodySecondary" css={styles.oneLine} color="supportText">
              {description}
            </Typography2>
          )}
        </div>
      </div>
      {isCurrent && (
        <React.Fragment>
          <CheckIcon size="medium" color="interactive" css={styles.checkIcon} />
          <VisuallyHidden>{_t('Selected')}</VisuallyHidden>
        </React.Fragment>
      )}
    </Link>
  );
};

export default ProgramCard;
